import type { BadgeProps } from '@insio/ui-toolkit';
import type { DeliveryStatus, GoodsReceiptStatus, InvoicedStatus } from '@insio/shared-types';

type BadgeColor = NonNullable<BadgeProps['color']>;

/**
 * Keyed off what the status means, not where it sits in the lifecycle; `RECEIVED` is finished.
 * A requisition's and an order's colours are their display statuses' (`*-display-status.ts`).
 */
const GOODS_RECEIPT_COLORS: Record<GoodsReceiptStatus, BadgeColor> = {
  DRAFT: 'neutral',
  RECEIVED: 'success',
  CANCELLED: 'error'
};

/** A cancelled requisition or order, in place of its status colour — final, like a refusal. */
export const CANCELLED_BADGE_COLOR: BadgeColor = 'error';

/** A completed requisition or order, in place of its status colour — the CompletedBadge colour. */
export const COMPLETED_BADGE_COLOR: BadgeColor = 'success';

/** An archived document — neutral, as the archived invoice reads. */
export const ARCHIVED_BADGE_COLOR: BadgeColor = 'neutral';

export function goodsReceiptStatusColor(status: GoodsReceiptStatus): BadgeColor {
  return GOODS_RECEIPT_COLORS[status] ?? 'neutral';
}

/** Done is green, under way a warning, not started quiet — the same scale for both. */
const DELIVERY_COLORS: Record<DeliveryStatus, BadgeColor> = {
  NOT_DELIVERED: 'neutral',
  PARTIALLY_DELIVERED: 'warning',
  DELIVERED: 'success'
};

const INVOICED_COLORS: Record<InvoicedStatus, BadgeColor> = {
  NOT_INVOICED: 'neutral',
  PARTIALLY_INVOICED: 'warning',
  INVOICED: 'success'
};

export function deliveryStatusColor(status: DeliveryStatus): BadgeColor {
  return DELIVERY_COLORS[status] ?? 'neutral';
}

export function invoicedStatusColor(status: InvoicedStatus): BadgeColor {
  return INVOICED_COLORS[status] ?? 'neutral';
}
